import { FC, SyntheticEvent, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import { Entity } from 'domain/types/Entity';
import { Airport } from 'domain/entities/Airport';
import { useCaseSearchAirport } from 'application/stations/useCases/useCaseSearchAirport';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import DEFAULT_DEBOUNCE_TIME from 'infrastructure/utils/defaultDebounceTime';
import { Autocomplete } from 'targets/web/components';

export const AirportMultiAutocomplete: FC<{
  initialValue?: Entity<Airport>[];
  onChange: (airports: Entity<Airport>[]) => void;
}> = ({ initialValue, onChange }) => {
  const t = useTranslationPrefix('components.filters_container');

  const [airportPhrase, setAirportPhrase] = useState('');
  const { items, isLoading } = useCaseSearchAirport({ phrase: airportPhrase }, true);

  const handleInputChange = useMemo(
    () =>
      debounce((event: SyntheticEvent, newInputValue: string) => {
        if (event?.type !== 'change' && newInputValue) {
          return;
        }

        setAirportPhrase(newInputValue);
      }, DEFAULT_DEBOUNCE_TIME),
    [],
  );

  return (
    <Autocomplete<Entity<Airport>, true, false, false>
      label={t('airport_label')}
      loading={isLoading}
      options={items}
      getOptionLabel={(option) => option.name}
      defaultValue={initialValue}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      data-testname="airportFilter"
      onChange={(_, value) => {
        onChange(value);
      }}
      placeholder={t('airport_placeholder')}
      multiple
    />
  );
};
